import { Option } from '@sapphire/result';
import { BaseMapper, type Nullable } from '@ps/utils';
import { SupplierBlendDto } from '../dtos/supplier-blend.dto';
import {
  SupplierBlend,
  SupplierBlendFlexibilityMode,
  SupplierBlendGroup,
} from '../../models/supplier-blend.model';

export class SupplierBlendMapper extends BaseMapper<
  SupplierBlendDto,
  SupplierBlend
> {
  public fromDto(dto: SupplierBlendDto): SupplierBlend {
    const groups = this._mapGroups(dto);

    return {
      id: dto.id,
      name: dto.config_name,
      isDefault: dto.is_default,
      isLocked: dto.is_locked,
      groupNames: dto.suppliers.map((supplier) => supplier.group_name),
      locale: dto.locale,
      flexibilityMode: this._mapFlexibilityMode(dto, groups),
      globalFlexibilityPercentage: dto.blend_flexibility,
      groups,
    };
  }

  private _mapGroups(dto: SupplierBlendDto): SupplierBlendGroup[] {
    return dto.suppliers.map((supplier) => ({
      groupName: supplier.group_name,
      members: supplier.supplier_id.map((member) => ({
        id: member.id,
        name: member.name,
        allocation: this._mapNumericValue(member.allocation),
      })),
      allocationPercentage: this._mapNumericValue(
        supplier.allocation_percentage,
      ),
      flexibilityPercentage: this._mapNumericValue(
        supplier.flexibility_percentage,
      ),
      isCustomAllocation: supplier.is_custom_allocation,
    }));
  }

  private _mapNumericValue(
    value: Nullable<number | string> | undefined,
  ): Nullable<number> {
    return Option.from(value)
      .map(Number)
      .filter((numericValue) => !isNaN(numericValue))
      .unwrapOr(null);
  }

  private _mapFlexibilityMode(
    dto: SupplierBlendDto,
    groups: SupplierBlendGroup[],
  ): SupplierBlendFlexibilityMode {
    if (dto.flexibilitySelectedType) {
      switch (dto.flexibilitySelectedType.key) {
        case 1:
          return 'selective';
        case 2:
          return 'none';
        default:
          return 'global';
      }
    }
    return groups.some((group) =>
      Option.from(group.flexibilityPercentage).isSome(),
    )
      ? 'selective'
      : 'global';
  }
}
